<template>
  <div v-if="selectedAccount" class="header-account" ref="panelRef">
    <div class="header-account-name">
      <AccountSelectorModal
        v-if="showAccountModal"
        v-on:confirm="selectAccountAndCloseModal"
        v-on:cancel="closeAccountSelectorModal" />
      <div data-cy="account-name-button" data-hj-suppress>
        <BackstageButton
          class="header-account-name-avatar"
          :variant="ButtonVariant.SECONDARY"
          icon-right="chevron-down"
          :key="selectedAccount.id"
          :title="selectedAccount.name"
          v-on:click="openAccountSelectorModal">
          <div class="cut-text">{{ selectedAccount.name }}</div>
        </BackstageButton>
      </div>
    </div>
    <div class="header-account-actions">
      <FaqPanel v-model="showPanel.faq" />
      <NotificationPanel v-model="showPanel.notification" v-if="isTrackingNotificationEnabled" />
      <AccountPanel v-model="showPanel.account" v-if="!hideAccountPanel" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAnalytics } from '@/composables/analytics';
import { useUserStore } from '@/stores/user';
import { onClickOutside } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, reactive, ref, watchEffect } from 'vue';
import { useRouter } from 'vue-router';

import BackstageButton from '@/components/common/BackstageButton.vue';
import { ButtonVariant } from '@/models/button-variant.enum';
import { GaCustomEvents } from '@/models/ga-custom-events.enum';

import AccountSelectorModal from './account-selector-modal.vue';

import AccountPanel from './AccountPanel.vue';
import FaqPanel from './FaqPanel.vue';
import NotificationPanel from './NotificationPanel.vue';

export interface Props {
  hideAccountPanel?: boolean;
}

withDefaults(defineProps<Props>(), {
  hideAccountPanel: false,
});

const isTrackingNotificationEnabled = import.meta.env.VITE_APP_trackingNotificationEnabled === 'true';

const userStore = useUserStore();
const { selectAccount, selectedAccountManually } = userStore;
const { userProfile, hasManuallySelectedAnAccount, selectedAccount } = storeToRefs(userStore);
const openAccountPopup = ref(false);

const router = useRouter();
const { pushAnalyticsEvent } = useAnalytics();

function selectAccountAndCloseModal(selectedAccountId: string) {
  selectedAccountManually();
  openAccountPopup.value = false;

  const isDifferentAccount = selectedAccountId !== selectedAccount.value?.id;
  const isPreSelectedAccount =
    userProfile.value?.accounts?.[0].id === selectedAccountId && hasManuallySelectedAnAccount;

  if (!isDifferentAccount && !isPreSelectedAccount) {
    return;
  }

  selectAccount(selectedAccountId);

  if (isPreSelectedAccount) {
    router.replace('/');
  }

  pushAnalyticsEvent({
    event: GaCustomEvents.ChangeAccount,
    web_user_id: userProfile.value?.user?.id,
    web_account_id: selectedAccount.value?.id,
    web_account_country: selectedAccount?.value?.billingCountry,
  });
}

function closeAccountSelectorModal() {
  openAccountPopup.value = false;
  selectedAccountManually();
}

function openAccountSelectorModal() {
  openAccountPopup.value = true;
}

const showAccountModal = computed<boolean>(() => {
  return !hasManuallySelectedAnAccount.value || openAccountPopup.value;
});

enum PanelType {
  FAQ = 'faq',
  ACCOUNT = 'account',
  NOTIFICATION = 'notification',
}

const showPanel = reactive({
  [PanelType.FAQ]: false,
  [PanelType.ACCOUNT]: false,
  [PanelType.NOTIFICATION]: false,
});

const closePanels = () => {
  Object.keys(showPanel).forEach((key) => {
    showPanel[key as PanelType] = false;
  });
};

const closeOtherPanels = (panel: keyof typeof showPanel) => {
  if (showPanel[panel]) {
    closePanels();
    showPanel[panel] = true;
  }
};

watchEffect(() => {
  closeOtherPanels(PanelType.ACCOUNT);
});

watchEffect(() => {
  closeOtherPanels(PanelType.NOTIFICATION);
});

watchEffect(() => {
  closeOtherPanels(PanelType.FAQ);
});

const panelRef = ref(null);
onClickOutside(panelRef, () => {
  closePanels();
});
</script>

<style scoped lang="scss">
.header-account {
  display: flex;
  flex-shrink: 9999;
  align-items: center;
  gap: 10px;
  margin: 0 0.125rem 0 auto;
  position: relative;

  &-name {
    &-avatar {
      .cut-text {
        max-width: 250px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        @include breakpoint(untilxlarge) {
          max-width: 87px;
        }
      }
    }
  }

  &-actions {
    z-index: 90;
    display: flex;
    gap: 10px;
  }
}
</style>
